import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Ticket as TicketIcon, Loader2 } from "lucide-react";
import { endpoints, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Tickets() {
  const [tiers, setTiers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [buying, setBuying] = useState(null);
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const { user, login } = useAuth();

  useEffect(() => { endpoints.ticketTiers().then(setTiers).catch(() => {}); }, []);
  const loadTickets = useCallback(() => { if (user) api.get("/my-tickets").then((r) => setTickets(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts > 6) { setPolling(false); toast.error("Payment status check timed out."); return; }
    setPolling(true);
    try {
      const res = await api.get(`/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPolling(false); toast.success("Payment successful! Ticket added."); loadTickets();
        params.delete("session_id"); setParams(params, { replace: true });
        return;
      }
      if (res.data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setPolling(false); }
  }, [loadTickets, params, setParams]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) pollStatus(sid);
  }, [params, pollStatus]);

  const buy = async (tier) => {
    if (!user) return login();
    setBuying(tier.id);
    try {
      const res = await api.post("/checkout", { tier_id: tier.id, quantity: 1, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch {
      toast.error("Could not start checkout");
      setBuying(null);
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-4xl uppercase text-white">Tickets</h1>
      {polling && (
        <div className="border border-[#ff7a1a]/50 bg-[#121212] p-4 flex items-center gap-3 text-white" data-testid="payment-polling">
          <Loader2 className="animate-spin text-[#ff7a1a]" size={18} /> Confirming your payment...
        </div>
      )}

      {tickets.length > 0 && (
        <div>
          <div className="font-heading uppercase text-sm text-white mb-2">My tickets</div>
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.id} className="flex items-center gap-3 border border-[#ffd600]/40 bg-[#121212] p-4" data-testid="my-ticket">
                <div className="w-11 h-11 pas-gradient grid place-items-center text-black"><TicketIcon size={20} /></div>
                <div className="flex-1">
                  <div className="font-heading text-white uppercase">{t.tier_name} × {t.quantity}</div>
                  <div className="text-[#8a8d91] text-xs">${t.amount} AUD · confirmed</div>
                </div>
                <Check className="text-[#ffd600]" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {tiers.map((t) => (
          <div key={t.id} className="border border-white/10 bg-[#121212] p-6 card-hover flex flex-col" data-testid={`tier-${t.id}`}>
            <div className="font-heading uppercase text-white text-lg">{t.name}</div>
            <div className="font-display text-5xl pas-gradient-text my-2">${t.price}<span className="text-lg text-[#8a8d91]"> AUD</span></div>
            <ul className="space-y-1.5 text-sm text-[#8a8d91] font-body flex-1 mb-4">
              {t.perks.map((p, i) => <li key={i} className="flex gap-2"><Check size={15} className="text-[#ffd600] shrink-0 mt-0.5" /> {p}</li>)}
            </ul>
            <button onClick={() => buy(t)} disabled={buying === t.id} className="pas-btn w-full text-sm" data-testid={`buy-${t.id}`}>
              {buying === t.id ? "Redirecting..." : "Buy ticket"}
            </button>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-[#8a8d91] font-body">Payments processed securely via Stripe. Test mode.</p>
    </div>
  );
}
